import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setPageOptions } from 'reducers/layout';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import { DragDropContext } from 'react-beautiful-dnd';
import { browserHistory } from 'react-router';
import { useMediaQuery } from 'react-responsive';
import BarnSheetsContainer from '../BarnSheetsContainer';
import Panel from 'components/Panel';
import Button from 'components/Button';
import ColumnsDropZone from './ColumnsDropZone';
// utils
import classnames from 'classnames/bind';
import { setColumnsToStorage } from 'utils/localStorageHelper';
import { getColumnsKeys, buildColumnLists, reorderList, moveToOtherList } from './columnsHelper';
import { getInitialColumnKeys, manageableTableNames } from './columnsConfig';
// styles
import styles from './BarnSheetsManageColumns.module.scss';

const cn = classnames.bind(styles);

const BarnSheetsManageColumns = ({ location, measurementSystem, setBreadcrumbs, setPageOptions }) => {
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const name = location.query?.name;
  const isValidTable = manageableTableNames.includes(name);
  const [searchValue, setSearchValue] = useState('');
  const [keysState, setKeysState] = useState(() => (isValidTable ? getColumnsKeys(name) : null));
  // keep latest keys for the header Save button, whose handler is set once on mount
  const keysRef = useRef(keysState);
  keysRef.current = keysState;

  const handleCancel = () => browserHistory.goBack();
  const handleSave = () => {
    setColumnsToStorage(keysRef.current, name);
    browserHistory.goBack();
  };

  useEffect(() => {
    if (!isValidTable) {
      browserHistory.replace('/barnsheets/groups');
      return undefined;
    }
    setBreadcrumbs([
      { label: <FormattedMessage id="general.pageTitle.barnsheets" />, path: '/barnsheets/groups' },
      { label: <FormattedMessage id="general.btn.manageFields" />, useLabelAsMobileTitle: true },
    ]);
    // mobile: actions live in the global header (Cancel = back, Save = right button).
    // desktop keeps them in the in-page heading, so clear the header slots there.
    if (isDesktop) {
      setPageOptions({ backLink: '', rightButton: '' });
      return undefined;
    }
    setPageOptions({
      backLink: true,
      rightButton: { label: 'save', onClick: handleSave },
    });
    return () => setPageOptions({ backLink: '', rightButton: '' });
  }, [isDesktop]);

  if (!isValidTable) return null;

  const { fixed, manageable, hidden } = buildColumnLists(name, keysState);

  const getLabelId = (column) => (
    (measurementSystem === 'metric' && column.metricLabelId) || column.labelId
  );
  const getLabelText = (column) => formatMessage({ id: getLabelId(column) }, column.labelValues);
  const isMatchSearch = (column) => getLabelText(column).toLowerCase().includes(searchValue.toLowerCase());
  const isListEmpty = (list) => (searchValue ? !list.some(isMatchSearch) : !list.length);

  const withLabels = (list) => list.map((column) => ({
    ...column,
    label: <FormattedMessage id={getLabelId(column)} values={column.labelValues} />,
  }));

  const onDragEnd = ({ source, destination }) => {
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      if (source.index === destination.index) return;
      const reordered = reorderList(keysState[source.droppableId], source.index, destination.index);
      setKeysState((prev) => ({ ...prev, [source.droppableId]: reordered }));
      return;
    }

    const [newSource, newDestination] = moveToOtherList(
      keysState[source.droppableId],
      keysState[destination.droppableId],
      source.index,
      destination.index,
    );
    setKeysState((prev) => ({
      ...prev,
      [source.droppableId]: newSource,
      [destination.droppableId]: newDestination,
    }));
  };

  const handleReset = () => setKeysState(getInitialColumnKeys(name));

  return (
    <BarnSheetsContainer>
      <Panel className="panel-padding">
        {isDesktop && (
          <Panel.Heading
            className={cn('sticky-heading')}
            renderTitle={() => (
              <h2 className="lighter">
                <FormattedMessage id="general.btn.manageFields" />
              </h2>
            )}
          >
            <div className={cn('header-actions')}>
              <Button light className="mr-10" onClick={handleCancel}>
                <FormattedMessage id="general.button.cancel" />
              </Button>
              <Button primary onClick={handleSave}>
                <FormattedMessage id="general.button.save" />
              </Button>
            </div>
          </Panel.Heading>
        )}
        <Panel.Body>
          <div className={cn('manage-columns')}>
            <div className={cn('search-section')}>
              <i className={cn('fa fa-search', 'search-icon')} />
              <input
                className={cn('search-input')}
                placeholder={formatMessage({ id: 'general.search' })}
                value={searchValue}
                onChange={({ target: { value } }) => setSearchValue(value)}
              />
              {searchValue && (
                <i
                  className={cn('fa fa-times', 'clear-icon')}
                  onClick={() => setSearchValue('')}
                />
              )}
            </div>

            <div className={cn('fields-title')}>
              <span className="semibold">
                <FormattedMessage id="barnsheets.manageColumns.defaultFields" />
              </span>
              <span className={cn('reset-btn')} onClick={handleReset}>
                <FormattedMessage id="general.resetAllDefaults" />
              </span>
            </div>
            {fixed.filter(isMatchSearch).map((column) => (
              <div key={column.id} className={cn('default-field')}>
                <FormattedMessage id={getLabelId(column)} values={column.labelValues} />
                <span className={cn('field-status')}>
                  <FormattedMessage id="general.default" />
                </span>
              </div>
            ))}
            {isListEmpty(fixed) && (
              <div className={cn('placeholder')}>
                <FormattedMessage id="barnsheets.manageColumns.noFieldsMatchSearch" />
              </div>
            )}

            <DragDropContext onDragEnd={onDragEnd}>
              <div className={cn('fields-title')}>
                <span className="semibold">
                  <FormattedMessage id="barnsheets.manageColumns.manageableFields" />
                </span>
              </div>
              <ColumnsDropZone
                droppableId="manageable"
                fields={withLabels(manageable)}
                isEmpty={isListEmpty(manageable)}
                matchSearch={isMatchSearch}
                label={<FormattedMessage id="general.visible" />}
                placeholder={(
                  <FormattedMessage
                    id={searchValue
                      ? 'barnsheets.manageColumns.noFieldsMatchSearch'
                      : 'barnsheets.manageColumns.noVisibleFields'}
                  />
)}
              />

              <div className={cn('hidden-fields-title')}>
                <hr />
                <span className={cn('text')}>
                  <FormattedMessage id="barnsheets.manageColumns.hiddenFields" />
                </span>
              </div>
              <ColumnsDropZone
                droppableId="hidden"
                fields={withLabels(hidden)}
                isEmpty={isListEmpty(hidden)}
                matchSearch={isMatchSearch}
                label={<FormattedMessage id="general.hidden" />}
                placeholder={(
                  <FormattedMessage
                    id={searchValue
                      ? 'barnsheets.manageColumns.noFieldsMatchSearch'
                      : 'barnsheets.manageColumns.noHiddenFields'}
                  />
)}
              />
            </DragDropContext>
          </div>
        </Panel.Body>
      </Panel>
    </BarnSheetsContainer>
  );
};

BarnSheetsManageColumns.propTypes = {
  location: T.object.isRequired,
  measurementSystem: T.string.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
};

export default connect(
  (state) => ({
    measurementSystem: state.auth.user.current_company.measurement_system,
  }), { setBreadcrumbs, setPageOptions }
)(BarnSheetsManageColumns);
